import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Mail } from 'lucide-react';
import { Button, Card, PageHeader, Section } from '@/components/ui';
import { InferenceShiftChart } from '@/components/charts';
import { mission, vision } from '@/content/strategy';
import { SITE } from '@/lib/site';

const commitments = [
  ['Measured, not claimed', 'Every performance figure comes from a paired run with the method attached, and the caveats travel with the number.'],
  ['Private by default', 'The stack runs inside your environment, on your terms. No third party’s licence, connectivity or permission is needed to install it.'],
  ['One accountable party', 'Framework, engine, Harness and connectors from one vendor. A defect is ours to fix, whichever layer it sits in.'],
];


export function AboutPage() {
  return (
    <div>
      <PageHeader
        kicker="About · FNK Labs"
        title={<span>One stack, built as one system.</span>}
        lead="Fnk is an agent framework, a control layer and an inference engine built as one non-blocking system — and the Harness that turns them into a governed service. DialogMe, its first customer, runs on it in production."
      />

      <Section n="01" kicker="Vision and mission" title="Where we are going, and what we do to get there.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Card variant="accent" className="p-6 sm:p-8">
            <p className="label mb-3">Vision</p>
            <p className="text-lg font-semibold leading-snug tracking-tight sm:text-xl">{vision.statement}</p>
          </Card>
          <Card variant="accent" className="p-6 sm:p-8">
            <p className="label mb-3">Mission</p>
            <p className="text-lg font-semibold leading-snug tracking-tight sm:text-xl">{mission.statement}</p>
          </Card>
        </div>
        <Link to="/vision" className="mt-6 inline-block w-full sm:w-auto">
          <Button variant="secondary" className="w-full sm:w-auto" rightIcon={<ArrowRight className="h-4 w-4" />}>Vision, positioning and strategy</Button>
        </Link>
      </Section>

      <Section n="02" kicker="Why now" title="Inference is leaving the public cloud." tone="white">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card variant="raised" className="p-5 sm:p-8">
            <InferenceShiftChart />
          </Card>
          <div className="space-y-4 text-sm leading-relaxed text-ink-2">
            <p>
              Around 56% of enterprises now run or plan production inference on private infrastructure. EU AI Act transparency obligations now apply,
              so a deployment that produces its own evidence trail is now a procurement requirement.
            </p>
            <p>
              Buyers already budget software per accelerator, and OEMs need software to differentiate thin server margins. The position one layer down —
              the framework and engine a data centre standardises on — is unclaimed.
            </p>
          </div>
        </div>
      </Section>

      <Section n="03" kicker="How we build" title="Three commitments.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {commitments.map(([t, d]) => (
            <Card key={t} variant="plain" className="p-6">
              <h3 className="text-base font-semibold tracking-tight">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-2">{d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section n="04" kicker="First customer" title="DialogMe runs on Fnk." lead="DialogMe, a conversational AI platform, is Fnk’s first customer. Its production workload runs on the Fnk stack and hardens the framework and the engine against real traffic.">
        <a href={SITE.dialogme} target="_blank" rel="noopener noreferrer" className="inline-block w-full sm:w-auto">
          <Button variant="secondary" className="w-full sm:w-auto" rightIcon={<ArrowUpRight className="h-4 w-4" />}>Visit dialogme.ai</Button>
        </a>
      </Section>

      <section className="border-t border-line">
        <div className="mx-auto max-w-site px-4 py-14 sm:px-6 md:py-20">
          <Card variant="accent" className="flex flex-col gap-6 p-6 sm:p-8 md:flex-row md:items-center md:justify-between md:p-10">
            <div>
              <p className="label mb-2">Get in touch</p>
              <p className="max-w-xl text-sm leading-relaxed text-ink-2">
                Assessments, licences, boxes and partnerships. Investors:{' '}
                <Link to="/investors" className="text-accent-ink underline decoration-accent/40 underline-offset-2">/investors</Link>.
              </p>
            </div>
            <a href={`mailto:${SITE.sales}`} className="w-full md:w-auto">
              <Button variant="primary" size="lg" className="w-full md:w-auto" leftIcon={<Mail className="h-4 w-4" />} rightIcon={<ArrowRight className="h-4 w-4" />}>{SITE.sales}</Button>
            </a>
          </Card>
        </div>
      </section>
    </div>
  );
}
